import { forwardRef, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import c from 'classnames';

import { Icon } from '../Icon/Icon';
import Typography from '../Typography/Typography';

import styles from './THNumberField.module.css';

export interface NumberFieldProps {
  name: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void | Promise<boolean | void>;
  value?: number;
  disabled?: boolean;
  label: string;
  labelClasses?: string;
  labelInside?: boolean;
  // Accept error objects from any react-hook-form schema without requiring
  // callers to specify generics on this UI component.
  error?: FieldError;
  hideErrors?: boolean;
  placeholder?: string;
  center?: boolean;
  defaultValue?: number;
  description?: string;
  classes?: string;
  classNames?: string;
  min?: number;
  max?: number;
  step?: number;
  showRequiredIndicator?: boolean;
  required?: boolean;
}

export function THNumberFieldInner(
  props: NumberFieldProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const {
    label,
    labelClasses,
    labelInside = false,
    name,
    error,
    hideErrors,
    placeholder,
    center = true,
    defaultValue = 1,
    classes,
    classNames,
    description,
    min = 0,
    max = 100,
    step = 1,
    showRequiredIndicator = false,
    required,
    onChange,
    ...rest
  } = props;

  // Use the provided value prop if available, otherwise use defaultValue
  const [internalValue, setInternalValue] = useState<number>(
    props.value !== undefined ? props.value : defaultValue,
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue)) {
      setInternalValue(newValue);
    }
    if (onChange) {
      onChange(e);
    }
  };

  const handleIncrement = () => {
    const newValue = Math.min(internalValue + step, max);
    setInternalValue(newValue);

    // Manually trigger onChange event with the new value
    const event = {
      target: {
        name,
        value: newValue.toString(),
      },
    } as React.ChangeEvent<HTMLInputElement>;

    if (onChange) {
      onChange(event);
    }
  };

  const handleDecrement = () => {
    const newValue = Math.max(internalValue - step, min);
    setInternalValue(newValue);

    // Manually trigger onChange event with the new value
    const event = {
      target: {
        name,
        value: newValue.toString(),
      },
    } as React.ChangeEvent<HTMLInputElement>;

    if (onChange) {
      onChange(event);
    }
  };

  return (
    <div className={c(styles.field, classes, classNames)}>
      {!labelInside && (
        <label htmlFor={name} className={c(styles.label, labelClasses)}>
          <Typography variant='body-s'>
            {label}
            {showRequiredIndicator && (
              <span
                className={c(
                  styles.requiredIndicator,
                  required && styles.required,
                )}
              >
                {required ? '*' : ' (optional)'}
              </span>
            )}
          </Typography>
        </label>
      )}
      {!!description && (
        <Typography variant='label' className={styles.description}>
          {description}
        </Typography>
      )}
      <div className={styles.inputContainer}>
        <button
          type='button'
          className={c(styles.controlButton, styles.decrement)}
          onClick={handleDecrement}
          disabled={internalValue <= min}
          tabIndex={-1}
          aria-label={`Decrease ${label}`}
        >
          <Icon name='minus' size='s' />
        </button>

        <input
          type='number'
          id={name}
          name={name}
          placeholder={placeholder || '1'}
          aria-label={label}
          className={c(
            styles.input,
            !!error && styles.hasError,
            labelInside && styles.labelInside,
            {
              'text-center': center,
            },
          )}
          value={internalValue}
          onChange={handleChange}
          min={min}
          max={max}
          step={step}
          {...rest}
          ref={ref}
        />

        <button
          type='button'
          className={c(styles.controlButton, styles.increment)}
          onClick={handleIncrement}
          disabled={internalValue >= max}
          tabIndex={-1}
          aria-label={`Increase ${label}`}
        >
          <Icon name='plus' size='s' />
        </button>

        {labelInside && (
          <label
            htmlFor={name}
            className={c(styles.label, labelClasses, styles.inputInset)}
          >
            <Typography variant='label'>
              {label}
              {showRequiredIndicator && required && (
                <span className={styles.requiredIndicator}>*</span>
              )}
            </Typography>
          </label>
        )}
      </div>
      {!hideErrors && (
        <div className={styles.errors}>
          {!!error && (
            <ErrorMessage
              errors={{ [name]: error }}
              name={name}
              render={({ message }: { message: string }) => (
                <div
                  className={styles.error}
                  id={`error-${name}`}
                  key={message}
                  role='alert'
                >
                  <Icon name='alertCircle' size='xs' /> {message}
                </div>
              )}
            />
          )}
        </div>
      )}
    </div>
  );
}

export const THNumberField = forwardRef(THNumberFieldInner);
export default THNumberField;
